import React, {useEffect} from 'react'
import {useHistory} from 'react-router-dom'
import {LoadingState} from '../../components'
import {useAuth} from '../../stores/AuthStore'
import {
  ALBUMS_PATH,
  AUTH_ERROR_PATH,
  clearReturnPath,
  takeReturnPath,
} from '../../navigation'

/*
This is where we redirect to after Auth0 authentication.
We will use the auth/exchange endpoint to send our access_token to get the jwt legacy token.
If we get a good legacy token, then redirect to wherever the user was originally
headed — or to /albums when they arrived with no destination in mind (AB#25795).
*/

export const CallbackView: React.FC<React.PropsWithChildren> = () => {
  const history = useHistory()
  const {error, authenticationStatus, loading} = useAuth()

  useEffect(() => {
    // If we are still loading, don't navigate — and in particular don't consume
    // the stash, or a second pass of this effect would find it already spent.
    if (loading) {
      return
    }

    // If there is an error, navigate to the auth error page
    if (error) {
      // The login this path was captured for has failed, so the destination is
      // dead. Leaving it would silently redirect the next successful login.
      clearReturnPath()
      history.replace(AUTH_ERROR_PATH)
      return
    }

    // If we are authenticated with Auth0, return the user to the page they
    // originally asked for. takeReturnPath validates on the way out and yields
    // undefined for anything stale, unsafe, or absent, so the historical
    // /albums default still covers a plain login.
    if (
      authenticationStatus === 'idp_authenticated' ||
      authenticationStatus === 'authenticated'
    ) {
      history.replace(takeReturnPath() ?? ALBUMS_PATH)
    }
  }, [authenticationStatus, loading, error, history])

  return <LoadingState />
}
